import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, UserPlus, Loader2, AlertCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

/**
 * Admin-only "Add customer manually" modal.
 *
 * Fills the gap for customers who never lived in Syncro — e.g. legacy
 * paper handovers or off-platform signups — so admins don't have to
 * pretend they came in through the wizard.
 *
 * Under the hood this is just a thin form over
 * ``POST /commissions/migrations/{partner_code}/clients`` (source=manual).
 * The endpoint idempotently pushes to Syncro if the partner's board has
 * ``syncro_sync_enabled`` — no need for us to touch that here.
 */
const STAGES = [
  { key: "not_contacted", label: "Not contacted" },
  { key: "contacted", label: "Contacted" },
  { key: "info_sent", label: "Info sent" },
  { key: "form_submitted", label: "Form submitted" },
  { key: "provisioning", label: "Provisioning" },
  { key: "installed", label: "Installed" },
  { key: "live", label: "Live" },
  { key: "completed", label: "Completed" },
  { key: "lost", label: "Lost or declined" },
];

export default function ManualAddCustomerModal({ onClose, onCreated }) {
  const [partners, setPartners] = useState(null);
  const [form, setForm] = useState({
    partner_code: "",
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    service_address: "",
    chosen_plan: "",
    avc_number: "",
    stage: "not_contacted",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/commissions/partners");
        const active = (data || []).filter((p) => p.active !== false);
        setPartners(active);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load partners");
        setPartners([]);
      }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!form.partner_code) {
      toast.error("Pick a partner first");
      return;
    }
    if (!form.business_name.trim() && !form.contact_name.trim()) {
      toast.error("At least one of business name or contact name is required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        business_name: form.business_name.trim(),
        contact_name: form.contact_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        service_address: form.service_address.trim(),
        chosen_plan: form.chosen_plan.trim(),
        avc_number: form.avc_number.trim(),
        stage: form.stage,
        notes: form.notes.trim(),
      };
      // Strip empty strings so we don't overwrite defaults with blanks
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      const { data } = await api.post(
        `/commissions/migrations/${form.partner_code}/clients`,
        payload,
      );
      toast.success(`Added ${data.business_name || data.contact_name || "customer"}`);
      onCreated?.(data);
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Create failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm"
      data-testid="manual-add-customer-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[92vh] flex flex-col rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              Manual entry
            </div>
            <h3 className="text-slate-100 text-base font-semibold inline-flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-cyan-300" /> Add customer
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="manual-add-customer-close"
            className="text-slate-400 hover:text-slate-100 p-1 rounded"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 flex items-start gap-2 text-[11px] text-amber-200">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Use this for customers that <b>never lived in Syncro</b>. If the partner&apos;s board has
              Syncro sync enabled, this record is best-effort pushed on save — otherwise it stays
              Mongo-only until you link it manually.
            </span>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
              Partner<span className="text-red-400">*</span>
            </label>
            <select
              value={form.partner_code}
              onChange={(e) => set("partner_code", e.target.value)}
              data-testid="manual-add-customer-partner"
              disabled={partners === null}
              className="w-full bg-slate-950 border border-slate-800 rounded-lg text-sm px-2 py-2 focus:border-cyan-500 outline-none"
              required
            >
              <option value="">
                {partners === null ? "Loading partners…" : "Select a partner"}
              </option>
              {(partners || []).map((p) => (
                <option key={p.code} value={p.code}>
                  {p.name} ({p.code})
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field
              label="Business name"
              testid="manual-add-customer-business"
              value={form.business_name}
              onChange={(v) => set("business_name", v)}
              placeholder="Acme Widgets Pty Ltd"
            />
            <Field
              label="Contact name"
              testid="manual-add-customer-contact"
              value={form.contact_name}
              onChange={(v) => set("contact_name", v)}
              placeholder="Jane Smith"
            />
            <Field
              label="Email"
              type="email"
              testid="manual-add-customer-email"
              value={form.email}
              onChange={(v) => set("email", v)}
              placeholder="jane@acme.example"
            />
            <Field
              label="Phone"
              testid="manual-add-customer-phone"
              value={form.phone}
              onChange={(v) => set("phone", v)}
              placeholder="0412 345 678"
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
              Service address
            </label>
            <AddressAutocomplete
              value={form.service_address}
              onChange={(v) => set("service_address", v)}
              placeholder="10 Example St, Sydney NSW 2000"
              data-testid="manual-add-customer-address"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field
              label="Chosen plan"
              testid="manual-add-customer-plan"
              value={form.chosen_plan}
              onChange={(v) => set("chosen_plan", v)}
              placeholder="NBN 100/40 Business"
            />
            <Field
              label="AVC number"
              testid="manual-add-customer-avc"
              value={form.avc_number}
              onChange={(v) => set("avc_number", v)}
              placeholder="AVC000123456789"
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
              Initial stage
            </label>
            <select
              value={form.stage}
              onChange={(e) => set("stage", e.target.value)}
              data-testid="manual-add-customer-stage"
              className="w-full bg-slate-950 border border-slate-800 rounded-lg text-sm px-2 py-2 focus:border-cyan-500 outline-none"
            >
              {STAGES.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
            <p className="text-[10px] text-slate-500 mt-1">
              Where this card lands on the partner&apos;s Kanban.
            </p>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
              Notes
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              rows={3}
              data-testid="manual-add-customer-notes"
              placeholder="Anything the ops team should know — e.g. porting a legacy PBX, priority customer, etc."
              className="w-full bg-slate-950 border border-slate-800 rounded-lg text-xs px-2 py-2"
            />
          </div>
        </form>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-800 bg-slate-950/40">
          <button
            type="button"
            onClick={onClose}
            data-testid="manual-add-customer-cancel"
            className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 text-xs font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !form.partner_code}
            data-testid="manual-add-customer-submit"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
            {busy ? "Adding…" : "Add customer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-slate-950 border border-slate-800 rounded-lg text-sm px-2 py-2 focus:border-cyan-500 outline-none"
      />
    </div>
  );
}
